import { supabase } from '../lib/supabase';

// ============================================================
// MAPPERS
// ============================================================
const fromTenderRow = (row) => ({
  id: row?.id,
  tenderName: row?.tender_name,
  tenderNumber: row?.tender_number,
  clientName: row?.client_name,
  projectName: row?.project_name,
  closingDate: row?.closing_date,
  submissionDate: row?.submission_date,
  estimatedValue: parseFloat(row?.estimated_value) || 0,
  tenderCategory: row?.tender_category,
  department: row?.department,
  description: row?.description,
  status: row?.status,
  complianceOverall: parseFloat(row?.compliance_overall) || 0,
  complianceTechnical: parseFloat(row?.compliance_technical) || 0,
  complianceCommercial: parseFloat(row?.compliance_commercial) || 0,
  complianceLegal: parseFloat(row?.compliance_legal) || 0,
  complianceResources: parseFloat(row?.compliance_resources) || 0,
  analysisResult: row?.analysis_result || null,
  executiveSummary: row?.executive_summary || '',
  createdBy: row?.created_by,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

const fromGapItemRow = (row) => ({
  id: row?.id,
  tenderId: row?.tender_id,
  category: row?.category,
  itemType: row?.item_type,
  itemName: row?.item_name,
  description: row?.description,
  gapStatus: row?.gap_status,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

const fromTaskRow = (row) => ({
  id: row?.id,
  tenderId: row?.tender_id,
  department: row?.department,
  title: row?.title,
  description: row?.description,
  assignee: row?.assignee,
  dueDate: row?.due_date,
  priority: row?.priority,
  taskStatus: row?.task_status,
  progress: row?.progress || 0,
  gapItemId: row?.gap_item_id,
  createdAt: row?.created_at,
  updatedAt: row?.updated_at,
});

// ============================================================
// TENDERS CRUD
// ============================================================
export const getTenders = async () => {
  const { data, error } = await supabase
    ?.from('tenders')
    ?.select('*')
    ?.order('created_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromTenderRow);
};

export const getTenderById = async (id) => {
  const { data, error } = await supabase
    ?.from('tenders')
    ?.select('*')
    ?.eq('id', id)
    ?.single();
  if (error) throw error;
  return fromTenderRow(data);
};

export const createTender = async (tender, userId) => {
  const { data, error } = await supabase
    ?.from('tenders')
    ?.insert({
      tender_name: tender?.tenderName,
      tender_number: tender?.tenderNumber,
      client_name: tender?.clientName,
      project_name: tender?.projectName,
      closing_date: tender?.closingDate || null,
      submission_date: tender?.submissionDate || null,
      estimated_value: parseFloat(tender?.estimatedValue) || 0,
      tender_category: tender?.tenderCategory || 'Other',
      department: tender?.department,
      description: tender?.description,
      status: 'draft',
      created_by: userId,
    })
    ?.select()
    ?.single();
  if (error) throw error;
  return fromTenderRow(data);
};

export const updateTender = async (id, updates) => {
  const payload = {};
  if (updates?.tenderName !== undefined) payload.tender_name = updates?.tenderName;
  if (updates?.tenderNumber !== undefined) payload.tender_number = updates?.tenderNumber;
  if (updates?.clientName !== undefined) payload.client_name = updates?.clientName;
  if (updates?.projectName !== undefined) payload.project_name = updates?.projectName;
  if (updates?.closingDate !== undefined) payload.closing_date = updates?.closingDate;
  if (updates?.submissionDate !== undefined) payload.submission_date = updates?.submissionDate;
  if (updates?.estimatedValue !== undefined) payload.estimated_value = parseFloat(updates?.estimatedValue) || 0;
  if (updates?.tenderCategory !== undefined) payload.tender_category = updates?.tenderCategory;
  if (updates?.department !== undefined) payload.department = updates?.department;
  if (updates?.description !== undefined) payload.description = updates?.description;
  if (updates?.status !== undefined) payload.status = updates?.status;
  if (updates?.complianceOverall !== undefined) payload.compliance_overall = updates?.complianceOverall;
  if (updates?.complianceTechnical !== undefined) payload.compliance_technical = updates?.complianceTechnical;
  if (updates?.complianceCommercial !== undefined) payload.compliance_commercial = updates?.complianceCommercial;
  if (updates?.complianceLegal !== undefined) payload.compliance_legal = updates?.complianceLegal;
  if (updates?.complianceResources !== undefined) payload.compliance_resources = updates?.complianceResources;
  if (updates?.analysisResult !== undefined) payload.analysis_result = updates?.analysisResult;
  if (updates?.executiveSummary !== undefined) payload.executive_summary = updates?.executiveSummary;

  const { data, error } = await supabase
    ?.from('tenders')
    ?.update(payload)
    ?.eq('id', id)
    ?.select()
    ?.single();
  if (error) throw error;
  return fromTenderRow(data);
};

export const deleteTender = async (id) => {
  const { error } = await supabase?.from('tenders')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
};

// ============================================================
// TENDER DOCUMENTS
// ============================================================
export const getTenderDocuments = async (tenderId) => {
  const { data, error } = await supabase
    ?.from('tender_documents')
    ?.select('*')
    ?.eq('tender_id', tenderId)
    ?.order('uploaded_at', { ascending: true });
  if (error) throw error;
  return (data || [])?.map((row) => ({
    id: row?.id,
    tenderId: row?.tender_id,
    fileName: row?.file_name,
    fileType: row?.file_type,
    fileSize: row?.file_size,
    fileUrl: row?.file_url,
    uploadedAt: row?.uploaded_at,
  }));
};

export const createTenderDocument = async (tenderId, doc) => {
  const { data, error } = await supabase
    ?.from('tender_documents')
    ?.insert({
      tender_id: tenderId,
      file_name: doc?.fileName,
      file_type: doc?.fileType,
      file_size: doc?.fileSize,
      file_url: doc?.fileUrl || null,
      file_data: doc?.fileData || null,
    })
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

// ============================================================
// GAP ITEMS
// ============================================================
export const getGapItems = async (tenderId) => {
  const { data, error } = await supabase
    ?.from('tender_gap_items')
    ?.select('*')
    ?.eq('tender_id', tenderId)
    ?.order('category', { ascending: true });
  if (error) throw error;
  return (data || [])?.map(fromGapItemRow);
};

export const createGapItems = async (tenderId, items) => {
  const rows = items?.map((item) => ({
    tender_id: tenderId,
    category: item?.category,
    item_type: item?.itemType,
    item_name: item?.itemName,
    description: item?.description || '',
    gap_status: 'pending',
  }));
  const { data, error } = await supabase
    ?.from('tender_gap_items')
    ?.insert(rows)
    ?.select();
  if (error) throw error;
  return (data || [])?.map(fromGapItemRow);
};

export const updateGapItemStatus = async (id, gapStatus) => {
  const { data, error } = await supabase
    ?.from('tender_gap_items')
    ?.update({ gap_status: gapStatus })
    ?.eq('id', id)
    ?.select()
    ?.single();
  if (error) throw error;
  return fromGapItemRow(data);
};

// ============================================================
// TASKS
// ============================================================
export const getTenderTasks = async (tenderId) => {
  const { data, error } = await supabase
    ?.from('tender_tasks')
    ?.select('*')
    ?.eq('tender_id', tenderId)
    ?.order('created_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map(fromTaskRow);
};

export const getAllTenderTasks = async () => {
  const { data, error } = await supabase
    ?.from('tender_tasks')
    ?.select('*, tenders(tender_name, tender_number)')
    ?.order('created_at', { ascending: false });
  if (error) throw error;
  return (data || [])?.map((row) => ({
    ...fromTaskRow(row),
    tenderName: row?.tenders?.tender_name,
    tenderNumber: row?.tenders?.tender_number,
  }));
};

export const createTenderTasks = async (tenderId, tasks) => {
  const rows = tasks?.map((task) => ({
    tender_id: tenderId,
    department: task?.department,
    title: task?.title,
    description: task?.description || '',
    assignee: task?.assignee || '',
    due_date: task?.dueDate || null,
    priority: task?.priority || 'medium',
    task_status: 'pending',
    progress: 0,
    gap_item_id: task?.gapItemId || null,
  }));
  const { data, error } = await supabase
    ?.from('tender_tasks')
    ?.insert(rows)
    ?.select();
  if (error) throw error;
  return (data || [])?.map(fromTaskRow);
};

export const updateTenderTask = async (id, updates) => {
  const payload = {};
  if (updates?.assignee !== undefined) payload.assignee = updates?.assignee;
  if (updates?.dueDate !== undefined) payload.due_date = updates?.dueDate;
  if (updates?.priority !== undefined) payload.priority = updates?.priority;
  if (updates?.taskStatus !== undefined) payload.task_status = updates?.taskStatus;
  if (updates?.progress !== undefined) payload.progress = updates?.progress;
  if (updates?.description !== undefined) payload.description = updates?.description;

  const { data, error } = await supabase
    ?.from('tender_tasks')
    ?.update(payload)
    ?.eq('id', id)
    ?.select()
    ?.single();
  if (error) throw error;
  return fromTaskRow(data);
};

// ============================================================
// DASHBOARD STATS
// ============================================================
export const getTenderDashboardStats = async () => {
  const { data: tenders, error } = await supabase
    ?.from('tenders')
    ?.select('status, compliance_overall, closing_date, tender_category, department');
  if (error) throw error;

  const { data: tasks, error: taskError } = await supabase
    ?.from('tender_tasks')
    ?.select('task_status, priority');
  if (taskError) throw taskError;

  const total = tenders?.length || 0;
  const open = tenders?.filter((t) => ['draft', 'analyzing', 'analyzed']?.includes(t?.status))?.length || 0;
  const closed = tenders?.filter((t) => ['submitted', 'won', 'lost', 'cancelled']?.includes(t?.status))?.length || 0;
  const analyzed = tenders?.filter((t) => t?.status === 'analyzed')?.length || 0;
  const avgCompliance = total > 0
    ? Math.round(tenders?.reduce((sum, t) => sum + (parseFloat(t?.compliance_overall) || 0), 0) / total)
    : 0;
  const highRisk = tenders?.filter((t) => (parseFloat(t?.compliance_overall) || 0) < 70)?.length || 0;
  const today = new Date();
  const upcoming = tenders?.filter((t) => {
    if (!t?.closing_date) return false;
    const diff = (new Date(t?.closing_date) - today) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  })?.length || 0;
  const pendingTasks = tasks?.filter((t) => t?.task_status === 'pending')?.length || 0;

  return {
    total,
    open,
    closed,
    analyzed,
    avgCompliance,
    highRisk,
    upcoming,
    pendingTasks,
    tenders: tenders || [],
    tasks: tasks || [],
  };
};
